import { Component, OnDestroy, OnInit, Renderer2, ViewChild, computed, effect, inject } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { Subscription, filter } from 'rxjs';

// Layout
import { WebLayoutService } from './services/web-layout.service';

import { WebSidebarComponent } from './base/sidebar/web-sidebar.component';
import { WebTopbarComponent } from './base/topbar/web-topbar.component';

// DevelopSoft Import
import { AuthStatus } from '../developsoft-web/interfaces/auth';
import { AuthService } from '../developsoft-web/services/auth/auth.service';


@Component({
  selector: 'app-web-layout',
  templateUrl: './web-layout.component.html',
  styles: [
  ]
})
export class WebLayoutComponent implements OnInit, OnDestroy {

  overlayMenuOpenSubscription: Subscription;
  menuOutsideClickListener: any;
  profileMenuOutsideClickListener: any;

  @ViewChild(WebSidebarComponent) webSidebar!: WebSidebarComponent;
  @ViewChild(WebTopbarComponent) webTopbar!: WebTopbarComponent;


  // Variables DevelopSoft
  private authService = inject( AuthService );
  private router = inject( Router );

  constructor(public layoutService: WebLayoutService, public renderer: Renderer2) {
      this.overlayMenuOpenSubscription = this.layoutService.overlayOpen$.subscribe(() => {
          if (!this.menuOutsideClickListener) {
              this.menuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                  const isOutsideClicked = !(this.webSidebar.el.nativeElement.isSameNode(event.target) || this.webSidebar.el.nativeElement.contains(event.target) 
                      || this.webTopbar.menuButton.nativeElement.isSameNode(event.target) || this.webTopbar.menuButton.nativeElement.contains(event.target));
                  
                  if (isOutsideClicked) {
                      this.hideMenu();
                  }
              });
          }

          if (!this.profileMenuOutsideClickListener) {
              this.profileMenuOutsideClickListener = this.renderer.listen('document', 'click', event => {
                  const isOutsideClicked = !(this.webTopbar.menu.nativeElement.isSameNode(event.target) || this.webTopbar.menu.nativeElement.contains(event.target)
                      || this.webTopbar.topbarMenuButton.nativeElement.isSameNode(event.target) || this.webTopbar.topbarMenuButton.nativeElement.contains(event.target));

                  if (isOutsideClicked) {
                      this.hideProfileMenu();
                  }
              });
          }

          if (this.layoutService.state.staticMenuMobileActive) {
              this.blockBodyScroll();
          }
      });

      this.router.events.pipe(filter(event => event instanceof NavigationEnd))
          .subscribe(() => {
              this.hideMenu();
              this.hideProfileMenu();
          });

  }

  ngOnInit() { 
    this.authStatusChangedEffect;
  }

  hideMenu() {
      this.layoutService.state.overlayMenuActive = false;
      this.layoutService.state.staticMenuMobileActive = false;
      this.layoutService.state.menuHoverActive = false;
      if (this.menuOutsideClickListener) {
          this.menuOutsideClickListener();
          this.menuOutsideClickListener = null;
      }
      this.unblockBodyScroll();
  }

  hideProfileMenu() {
      this.layoutService.state.profileSidebarVisible = false;
      if (this.profileMenuOutsideClickListener) {
          this.profileMenuOutsideClickListener();
          this.profileMenuOutsideClickListener = null;
      }
  }

  blockBodyScroll(): void {
      if (document.body.classList) {
          document.body.classList.add('blocked-scroll');
      }
      else {
          document.body.className += ' blocked-scroll';
      }
  }

  unblockBodyScroll(): void {
      if (document.body.classList) {
          document.body.classList.remove('blocked-scroll');
      }
      else {
          document.body.className = document.body.className.replace(new RegExp('(^|\\b)' +
              'blocked-scroll'.split(' ').join('|') + '(\\b|$)', 'gi'), ' ');
      }
  }

  get containerClass() {
      return {
          'layout-theme-light': this.layoutService.config.colorScheme === 'light',
          'layout-theme-dark': this.layoutService.config.colorScheme === 'dark',
          'layout-overlay': this.layoutService.config.menuMode === 'overlay',
          'layout-static': this.layoutService.config.menuMode === 'static',
          'layout-static-inactive': this.layoutService.state.staticMenuDesktopInactive && this.layoutService.config.menuMode === 'static',
          'layout-overlay-active': this.layoutService.state.overlayMenuActive,
          'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
          'p-input-filled': this.layoutService.config.inputStyle === 'filled',
          'p-ripple-disabled': !this.layoutService.config.ripple
      }
  }

  ngOnDestroy() {
      if (this.overlayMenuOpenSubscription) {
          this.overlayMenuOpenSubscription.unsubscribe();
      }

      if (this.menuOutsideClickListener) {
          this.menuOutsideClickListener();
      }
  }

  // Functions DevelopSoft

  public finishedAuthCheck = computed<boolean>( () => {
    
    if ( this.authService.authStatus() === AuthStatus.checking ) {
      return false;
    }

    return true;
  });

  public authStatusChangedEffect = effect(() => {

    switch( this.authService.authStatus() ) {

      case AuthStatus.checking:
        return;

      case AuthStatus.authenticated:
        this.router.navigateByUrl('/web');
        return;

      case AuthStatus.unAuthenticated:
        this.router.navigateByUrl('/auth/login');
        return;
    }
  });

}
